/**
 * Metadata types for events system
 * Includes visibility, formats, expertise levels, status options, etc.
 */

// Re-export Country from commonTypes for backwards compatibility
export type { Country } from "@shared/types/commonTypes";

// Filter option for dropdowns and filters
export interface FilterOption {
  label: string;
  value: string;
  flag?: string;
}

// Visibility status for events, tickets, instructors, etc.
export interface Visibility {
  label: string;
  description: string;
  slug: string;
  id: number;
}

// Ticket status options
export interface TicketStatus {
  label: string;
  description: string;
  slug: string;
}

// Event status options
export interface EventStatus {
  label: string;
  description: string;
  slug: string;
}

// Event format (workshop, conference, webinar, etc.)
export interface Format {
  id: string; // UUID
  slug: string;
  label: string;
  description: string;
}

// Expertise level for events
export interface ExpertiseLevel {
  id: string; // UUID
  label: string;
  description: string;
  slug: string;
}

// Instructor title/prefix (Dr., Prof., etc.)
export interface InstructorTitle {
  label: string;
  long: string;
  description: string;
  slug: string;
}

// Payment payout status
export interface PaymentPayout {
  label: string;
  description: string;
  slug: string;
}

// Payment processing platform
export interface PaymentProcessedPlatform {
  label: string;
  description: string;
  slug: string;
}

// Brand role (organizer, partner, sponsor, etc.)
export interface BrandRole {
  label: string;
  description: string;
  slug: string;
}

// Social media profile configuration
export interface SocialProfileConfig {
  slug: string;
  label: string;
  regex: string;
}

// Private note tone options
export interface PrivateNoteTone {
  label: string;
  description: string;
  slug: string;
}

// Event relation types (prerequisite, follow-up, etc.)
export interface EventRelationType {
  slug: string;
  label: string;
  description: string;
}

// Event status enum - kept for backward compatibility with CityField
export enum GeoAddressType {
  CITY = "city",
  ADDRESS = "address"
}

/**
 * Represents a geographic location with city and country information.
 * Used for address selection in various entities (venues, brands, etc.)
 * 
 * Maps to AddressDTO format from the API:
 * - googlePlaceId: Google Places API identifier
 * - latitude/longitude: Geographic coordinates
 * - address1: Street address (multilingual)
 * - summary: Full address summary (multilingual)
 * - city: City name (multilingual)
 * - country/countryISO3: Country information
 */
export interface GeoAddress {
  id?: number;
  googlePlaceId: string;
  type: GeoAddressType;
  summary: MultilingualText;
  city: MultilingualText;
  country: MultilingualText;
  countryISO3: string;
  cityId?: number|null;
  latitude?: number|null;
  longitude?: number|null;
  address1?: MultilingualText;
}

/**
 * Represents a translatable text field that supports multiple language codes.
 * Keys are language codes (e.g., 'bg', 'en', 'it') and values are the translated strings.
 */
export type MultilingualText = Record<string, string>;

// UUID type for format IDs and other UUID fields
export type UUID = string;

export type ImageDetailType = {
  src: string;
  size: number;
  type: string;
  width: number;
  height: number;
};

export interface MediaItem {
  type: "img" | "vid";
  name: MultilingualText;
  description?: MultilingualText;
  source: ImageDetailType[]|string;
}

export interface SocialProfile {
  social: string;
  url: string;
}

// Event category for topic/interest classification
export interface EventCategory {
  id: string;
  slug: string;
  name: string;
  description: string;
}

export enum StatusEntityType {
  EVENT = "event",
  INSTRUCTOR = "instructor",
  BRAND = "brand",
}
